import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useHasPermission } from '@/auth/auth-store';
import { usePublishFlow, useSaveFlow, useTerminalFlow } from '@/hooks/use-terminal-flows';
import { useT } from '@/i18n/use-t';
import { FlowCanvas } from './flow-canvas';
import { MonacoJsonEditor } from './monaco-json-editor';
import { parseRulesJson, type FlowRule } from './flow-vocab';

function extractError(err: unknown): string {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string } | undefined;
    return body?.message ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}

/**
 * RFL-131 — flow editor. The same `rules` array drives two views: a
 * graphical canvas (node-RED-style trigger→condition→action wires) and a
 * declarative JSON editor. Save uses optimistic concurrency
 * (expectedVersion); a 409 surfaces the conflict banner.
 */
export function FlowEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { data: flow, isLoading } = useTerminalFlow(id);
  const save = useSaveFlow();
  const publish = usePublishFlow();
  const canWrite = useHasPermission('terminal-flows.write');
  const t = useT('admin_ui');

  const [name, setName] = useState('');
  const [rules, setRules] = useState<FlowRule[]>([]);
  const [view, setView] = useState<'canvas' | 'json'>('canvas');
  const [jsonText, setJsonText] = useState('[]');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const lastInit = useRef<{ id: string; version: number } | null>(null);

  // (Re)initialise from the loaded flow — but preserve unsaved edits so a
  // background refetch doesn't wipe them (mirrors the hotkeys editor).
  useEffect(() => {
    if (flow === undefined) return;
    const changedIdentity =
      lastInit.current === null ||
      lastInit.current.id !== flow.id ||
      lastInit.current.version !== flow.version;
    if (!changedIdentity && dirty) return;
    setName(flow.name);
    setRules(flow.rules);
    setJsonText(JSON.stringify(flow.rules, null, 2));
    setJsonError(null);
    setDirty(false);
    lastInit.current = { id: flow.id, version: flow.version };
  }, [flow, dirty]);

  if (isLoading)
    return (
      <p className="text-sm text-muted-foreground">
        {t('terminalFlows.editor.loading', 'Loading flow…')}
      </p>
    );
  if (flow === undefined)
    return (
      <p className="text-sm text-destructive">
        {t('terminalFlows.editor.notFound', 'Flow not found.')}
      </p>
    );

  function applyRules(next: FlowRule[]): void {
    setRules(next);
    setJsonText(JSON.stringify(next, null, 2));
    setDirty(true);
    setOkMsg(null);
  }

  function onJsonChange(text: string): void {
    setJsonText(text);
    setDirty(true);
    setOkMsg(null);
    const parsed = parseRulesJson(text);
    if (parsed.ok) {
      setJsonError(null);
      setRules(parsed.rules);
    } else {
      setJsonError(parsed.error);
    }
  }

  function switchView(next: 'canvas' | 'json'): void {
    if (next === 'json') setJsonText(JSON.stringify(rules, null, 2));
    setView(next);
  }

  async function onSave(): Promise<void> {
    setBanner(null);
    setOkMsg(null);
    if (jsonError !== null) {
      setBanner(
        `${t('terminalFlows.editor.fixJsonFirst', 'Fix the JSON before saving:')} ${jsonError}`,
      );
      return;
    }
    try {
      const updated = await save.mutateAsync({
        id: flow!.id,
        expectedVersion: flow!.version,
        name,
        rules,
      });
      setOkMsg(
        t('terminalFlows.editor.saved', 'Saved (v{version}).').replace(
          '{version}',
          String(updated.version),
        ),
      );
      setDirty(false);
    } catch (err) {
      setBanner(extractError(err));
    }
  }

  async function onPublish(): Promise<void> {
    setBanner(null);
    setOkMsg(null);
    try {
      const updated = await publish.mutateAsync(flow!.id);
      setOkMsg(
        t(
          'terminalFlows.editor.published',
          'Published (v{version}). Terminals will pick it up on next sync.',
        ).replace('{version}', String(updated.version)),
      );
      setDirty(false);
    } catch (err) {
      setBanner(extractError(err));
    }
  }

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <Link to="/terminal-flows" className="text-xs text-muted-foreground hover:underline">
          ← {t('terminalFlows.title', 'Flow rules')}
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold">{flow.name}</h1>
          <Badge variant="outline">{flow.surface}</Badge>
          <Badge variant={flow.status === 'published' ? 'success' : 'default'}>{flow.status}</Badge>
          <span className="text-xs text-muted-foreground">v{flow.version}</span>
        </div>
      </header>

      {canWrite && (
        <div className="max-w-md space-y-1">
          <label className="text-xs text-muted-foreground">{t('terminalFlows.name', 'Name')}</label>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setDirty(true);
            }}
            maxLength={200}
          />
        </div>
      )}

      {/* View toggle */}
      <div className="flex items-center gap-1 rounded-md border border-border bg-secondary p-1 text-sm">
        <button
          type="button"
          className={
            view === 'canvas'
              ? 'rounded bg-primary px-3 py-1 text-primary-foreground'
              : 'rounded px-3 py-1'
          }
          onClick={() => switchView('canvas')}
        >
          {t('terminalFlows.editor.viewCanvas', 'Canvas')}
        </button>
        <button
          type="button"
          className={
            view === 'json'
              ? 'rounded bg-primary px-3 py-1 text-primary-foreground'
              : 'rounded px-3 py-1'
          }
          onClick={() => switchView('json')}
        >
          JSON
        </button>
      </div>

      {banner !== null && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {banner}
        </p>
      )}
      {okMsg !== null && (
        <p className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600">
          {okMsg}
        </p>
      )}

      {view === 'canvas' ? (
        <FlowCanvas surface={flow.surface} rules={rules} onChange={applyRules} />
      ) : (
        <div className="space-y-2">
          <MonacoJsonEditor value={jsonText} onChange={onJsonChange} />
          {jsonError !== null && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {jsonError}
            </p>
          )}
        </div>
      )}

      {canWrite && (
        <div className="flex items-center gap-2">
          <Button onClick={onSave} disabled={save.isPending || jsonError !== null}>
            {save.isPending
              ? t('common.saving', 'Saving…')
              : t('terminalFlows.editor.saveDraft', 'Save draft')}
          </Button>
          <Button variant="outline" onClick={onPublish} disabled={publish.isPending || dirty}>
            {publish.isPending
              ? t('terminalFlows.editor.publishing', 'Publishing…')
              : t('terminalFlows.editor.publish', 'Publish')}
          </Button>
          {dirty && (
            <span className="text-xs text-muted-foreground">
              {t('terminalFlows.editor.saveBeforePublishing', 'Save before publishing.')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
